import React, { useEffect, useState } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import {
  getToken,
  refreshAccessTokenAndStoreInCookie
} from 'common/services/token.service';

import AppRouter from 'common/AppRouter';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import {
  AUTH_FLOWS,
  LOCAL_STORAGE_KEYS,
  SESSION_STORAGE_KEYS
} from 'common/constants/constants';
import axios from 'axios';
import { LINB_API } from 'common/constants/api-urls';
import { isSurveyFlow } from 'common/utils/utils';

const localStorageAccessor = BrowserStorageAccessor().local;
const sessionStorageAccessor = BrowserStorageAccessor().session;

const axiosInstance = axios.create({});
const INIT_APP_STATE = {
  PENDING: 'pending',
  LOGOUT: 'logout',
  REDIRECT_TO_APP: 'redirect_to_app'
};

const redirectToLogin = (pathname, search) => {
  BrowserStorageAccessor().session.set(
    SESSION_STORAGE_KEYS.REDIRECT_TO,
    `${pathname}${search}`
  );
  return pathname === AppRouter.LOGIN.path ? null : (
    <Redirect to={{ pathname: AppRouter.LOGIN.path }} />
  );
};

const redirectToRegister = (pathname, search) => {
  BrowserStorageAccessor().session.set(
    SESSION_STORAGE_KEYS.REDIRECT_TO,
    `${pathname}${search}`
  );

  // If usser tries to access a Survey, redirect to login and not to signup
  if (isSurveyFlow()) {
    return <Redirect to={{ pathname: AppRouter.LOGIN.path }} />;
  }
  // If the current path is the login path, we don't need to redirect
  return pathname === AppRouter.LOGIN.path ? null : (
    <Redirect to={{ pathname: AppRouter.REGISTER.path }} />
  );
};

const logout = location => {
  const prevLoginProvider = localStorageAccessor.get(
    LOCAL_STORAGE_KEYS.LAST_PROVIDER_NAME
  );
  const authFlow = sessionStorageAccessor.get(SESSION_STORAGE_KEYS.AUTH_FLOW);
  const { pathname, search } = location;

  return prevLoginProvider || authFlow === AUTH_FLOWS.LINK
    ? redirectToLogin(pathname, search)
    : redirectToRegister(pathname, search);
};

const generateComponent = ({ location, children }, initAppState) => {
  return initAppState === INIT_APP_STATE.REDIRECT_TO_APP ? (
    <> {children} </>
  ) : initAppState === INIT_APP_STATE.LOGOUT ? (
    logout(location)
  ) : null;
};

const TokenHandler = ({ children }) => {
  const location = useLocation();
  const [initAppState, setInitAppState] = useState(INIT_APP_STATE.PENDING);
  const token = getToken();

  useEffect(() => {
    async function validateToken() {
      if (!token) {
        try {
          const { data } = await axiosInstance.get(LINB_API.TOKEN.REFRESH, {
            withCredentials: true
          });
          const accessToken = await refreshAccessTokenAndStoreInCookie(data);
          if (!accessToken) {
            return INIT_APP_STATE.LOGOUT;
          }
        } catch (e) {
          return INIT_APP_STATE.LOGOUT;
        }
      }
      return INIT_APP_STATE.REDIRECT_TO_APP;
    }

    validateToken()
      .then(initAppState => {
        setInitAppState(initAppState);
      })
      .catch(e => {
        // eslint-disable-next-line no-console
        console.error(e);
        setInitAppState(INIT_APP_STATE.LOGOUT);
      });
    return () => {};
  }, []);
  return generateComponent({ location, children }, initAppState);
};

export default TokenHandler;
